import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FormGroup, Row, Col, Label, Input } from "reactstrap";
import { useForm } from "react-hook-form";

import jsonData from "../../components/json/accountsType.json";
import countriesData from "../../components/json/countriesStates.json";
import industrySectorProfessions from "../../components/json/industrySectorProfessions.json";
import { toast } from "react-toastify";
import { clientService, activitiesServices } from "../../services";
import countries from "../../components/json/resitrictedCountries.json";
import TokenService from "../../services/token.service";
import IntlTelInput from "react-intl-tel-input";
import "react-intl-tel-input/dist/main.css";
// import { PiExclamationMarkDuotone } from "react-icons/pi";
import { Trans, useTranslation } from "react-i18next";
import Select, { components } from "react-select";

import { Button, CustomForm } from "../Form/style";

import uploadIcon from "../../assets/images/uploadIcon.svg";
import toolTipIcon from "../../assets/images/toopTip.jpg";

import "../Form/style.css";
import "../../index.css";

const INVALID_ICON_SIZE = 18;

const InvalidFeedbackIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 12 12"
    width={INVALID_ICON_SIZE}
    height={INVALID_ICON_SIZE}
    fill="none"
    stroke="#dc3545"
    aria-hidden="true"
  >
    <circle cx="6" cy="6" r="4.5" />
    <circle cx="6" cy="8.2" r="0.6" fill="#dc3545" stroke="none" />
    <path strokeLinejoin="round" d="M5.8 3.6h.4L6 6.5z" />
  </svg>
);

const RESIDENCE_COUNTRY_NAME = "UNITED ARAB EMIRATES";
const MAURITIUS_COUNTRY_NAME = "MAURITIUS";
const BANK_MODE_OF_PAYMENT = "Bank/Credit/Debit card/Transfer";
const isBankPaymentDefaultCountry = (country) =>
  country === RESIDENCE_COUNTRY_NAME || country === MAURITIUS_COUNTRY_NAME;
const PROFESSION_OTHER_LABEL = "Other (Requires Specification)";
const NOT_APPLICABLE_VALUE = "Not Applicable";

const stripBracketedText = (text) =>
  text ? text.replace(/\s*\([^)]*\)/g, "").trim() : "";

const FSC_RESTRICTED_COUNTRIES = new Set([
  "AFGHANISTAN",
  "CUBA",
  "MYANMAR",
  "PANAMA",
]);

// Full FSC entity exclusions for Country of Residence list
const FSC_ENTITY_RESTRICTED_COUNTRIES = new Set([
  ...FSC_RESTRICTED_COUNTRIES,
  "GHANA",
  "IRAN",
  "LIBYA",
  "SOUNTH SUDAN",
  "SYRIA",
  "USA",
  "YEMEN",
]);

const getNationalityCountries = (entity) => {
  if (entity === "FSC") {
    return countriesData.filter(
      (country) => !FSC_RESTRICTED_COUNTRIES.has(country.name)
    );
  }
  return countriesData;
};

const MOROCCO_LEGAL_PDF = {
  introductionAgreement:
    "https://godo.ae/wp-content/uploads/pdf-doc/GODO%20Agreement-3.pdf",
  privacyNotice:
    "https://godo.ae/wp-content/uploads/pdf-doc/PRIVACY%20NOTICE.pdf",
  dataProcessing:
    "https://godo.ae/wp-content/uploads/pdf-doc/DATA%20PROCESSING%20CONSENT%20FORM.pdf",
};

const employmentStatusCodes = {
  "Employed (Salaried)": "SAL",
  "Self-Employed": "SLF",
  "Business Owner/Director": "BOD",
  Retired: "RET",
  Student: "STU",
  "Unemployed/Homemaker": "UNE",
  Other: "OTH",
};

const uaeSourceOfFundsCodes = {
  Salary: "SAL",
  Pension: "PEN",
  "Profits/dividends": "PRO",
  "Sale of Assets": "SALE",
  "Inheritance & Gifts": "INH",
  Savings: "SAV",
  "Crypto investments": "CRYP",
  Other: "OTH",
};

const MODE_OF_PAYMENT_CODES = {
  Crypto: "CRYPTO",
  [BANK_MODE_OF_PAYMENT]: "NON_CRYPTO",
};

const industrySectorOptions = Object.keys(industrySectorProfessions);

const DROPDOWN_VALUE_FIELDS = new Set([
  "employmentStatus",
  "employmentStatusCode",
  "sourceOfFunds",
  "sourceOfFundsCode",
  "modeOfPayment",
  "modeOfPaymentCode",
  "industrySector",
  "profession",
]);

const resetEmploymentFields = (step2) => ({
  ...step2,
  employmentStatus: "",
  employmentStatusCode: "",
  sourceOfFunds: "",
  sourceOfFundsCode: "",
  employerBusinessName: "",
  industrySector: "",
  profession: "",
  specifyProfession: "",
});

const getTaxResidencyCountries = () =>
  countriesData.filter((country) => country.name !== RESIDENCE_COUNTRY_NAME);

const resetUaeLegalFields = (step2, step3, step4) => ({
  step2: {
    ...step2,
    taxResidency: "",
    otherCountryResidence: "",
  },
  step3: {
    ...step3,
    accountTradedByOthers: true,
    acceptTradingTerms: false,
  },
  step4: {
    ...step4,
    politicallyExposed: false,
    taxLiableOutsideUae: false,
    beneficialOwnerCode: "Y",
    infoCorrect: false,
    submitConsent: false,
    marketingEmail: false,
    marketingSms: false,
    marketingPhone: false,
    acknowledge: false,
  },
});

const resetDefaultAgreementFields = (step3, step4) => ({
  step3: {
    ...step3,
    accountTradedByOthers: true,
    powerOfAttorney: "",
    acceptTradingTerms: false,
  },
  step4: {
    ...step4,
    infoCorrect: false,
    submitConsent: false,
    marketingEmail: false,
    marketingSms: false,
    marketingPhone: false,
    acknowledge: false,
  },
});

const FormField = ({ label, children, className = "" }) => (
  <FormGroup className={`form-field mb-0 ${className}`}>
    <Label className="form-field__label">{label}</Label>
    {children}
  </FormGroup>
);

const FREE_TEXT_FIELDS = new Set([
  "employerBusinessName",
  "specifyProfession",
]);

export const MoroccoForm = ({ setLoading, loading, setSubmittedData }) => {
  const [refValue, setRefValue] = useState("");
  const [ptrValue, setPtrValue] = useState("");
  const [referrar, setReferrar] = useState("");
  const [agreementId, setAgreementId] = useState("");
  const [filteredAccountTypes, setFilteredAccountTypes] = useState("");
  const [emailError, setEmailError] = useState(0);
  const [imageList, setImageList] = useState([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const [countryList, setCountriesList] = useState([]);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [fileData, setFileData] = useState("");
  const [entityValue, setEntityValue] = useState(false);
  const [platForm, setPlatForm] = useState("MT5");
  const [utmMediumValue, setUtmMediumValue] = useState("");
  const [morocco, setMorocco] = useState(false);
  const [ipInfo, setIpInfo] = useState(null);
  const [email, setEmail] = useState({ type: "", value: "" });
  const [phoneError, setPhoneError] = useState(false);
  const [debouncedEmail, setDebouncedEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState(false);
  const [utmArray, setUtmArray] = useState({});
  const [couponCode, setCopponCode] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const initialLoad = useRef(true);

  const { t, i18n } = useTranslation();

  const { setValue, reset } = useForm();

  useEffect(() => {
    const fetchIPInfo = async () => {
      try {
        const response = await fetch(
          "https://ipinfo.io/json?token=52a7645817b83a"
        );
        const data = await response.json();
        setIpInfo(data);
      } catch (error) {
        console.error("Error fetching IP information:", error);
      }
    };

    fetchIPInfo();
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const refParam = searchParams.get("rm");
    const ptrParam = searchParams.get("ptr");
    let ibUrl = window.location.origin + `/?ptr=${ptrParam}&rm=${refParam}`;

    if (ibUrl === `${window.location.origin}/?ptr=2000095497&rm=69`) {
      window.location.href = `https://signup-ma.godocm.com/?rm=101&utm_medium=godoweb_ma`;
    }
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const refParam = searchParams.get("rm");
    const ptrParam = searchParams.get("ptr");
    const refAddParam = searchParams.get("ref");
    const agreementParam = searchParams.get("agr");
    const entityParam = searchParams.get("entity");
    const platform = searchParams.get("platform");
    const partner = searchParams.get("partner");
    const nationality = searchParams.get("nationality");
    const utmSource = searchParams.get("utm_source");
    const utmCategory = searchParams.get("utm_category");
    const utm = searchParams.get("utm_medium");
    const utmCampaign = searchParams.get("utm_campaign");

    setUtmArray({
      utmCampaign: utmCampaign || "",
      utmCategory: utmCategory || "",
      utmSource: utmSource || "",
      utmMedium: utmMediumValue || utm || "",
    });

    if (entityParam === "FSC") {
      const filterCountry = countriesData.filter(
        (country) => !FSC_ENTITY_RESTRICTED_COUNTRIES.has(country.name)
      );
      setCountriesList(filterCountry);
    } else if (entityParam === "SCA") {
      const filterCountry = countriesData.filter(
        (country) => country.name === "UNITED ARAB EMIRATES"
      );
      setCountriesList(filterCountry);
    } else {
      setCountriesList(countriesData);
    }

    if (nationality === "IRAN") {
      formData.step1.nationality = "IRAN";
      formData.step1.nationalityCode = "IR";
      formValueChange("step2", "nationality", "IRAN");
      formValueChange("step2", "nationalityCode", "IR");
    }

    if (nationality) {
      const formattedCountry =
        nationality.charAt(0) + nationality.slice(1).toLowerCase();
      const country = countries.find(
        (country) => country.name === formattedCountry
      );
      if (country) {
        formData.step1.country = nationality;
        formData.step1.countryCode = country.iso2;
        formValueChange("step1", "country", nationality);
        formValueChange("step1", "countryCode", country.iso2);
      }
    }

    setFilteredAccountTypes(filterAccountTypes("MT5"));
    // Parse the query parameters from the URL
    // Get the value of the 'ref' parameter
    if (partner === "traderscaptain") {
      setRefValue("95");
      setPtrValue("2000111910");
    }

    if (entityParam) {
      setEntityValue(entityParam);
    }

    // mobile app url
    if (
      platform === "MT5" &&
      utm === "godotrader_app" &&
      entityParam === "FSA" &&
      ipInfo !== null &&
      ipInfo.country === "MA"
    ) {
      window.location.href = `https://signup-ma.godocm.com/?entity=FSA_MA&utm_medium=godotrader_app&platform=MT5`;
    }

    if (
      platform === "MT5" &&
      utm === "godotrader_app" &&
      entityParam === "FSA_MA"
    ) {
      setPlatForm("MT5");
      setUtmMediumValue(utm);
      setMorocco(true);
      formData.step1.platform = "MT5";
      formData.step1.utmMedium = utm;
      setFilteredAccountTypes(filterAccountTypes("MT5"));
      const filterCountry = countriesData.filter(
        (country) => country.name === "MOROCCO"
      );
      setCountriesList(filterCountry);
    }

    if (
      platform === "MT5" &&
      utm === "godotrader_app" &&
      entityParam === "FSA"
    ) {
      setPlatForm("MT5");
      setUtmMediumValue(utm);
      formData.step1.platform = "MT5";
      formData.step1.utmMedium = utm;
      setFilteredAccountTypes(filterAccountTypes("MT5"));
    }

    //desktop url
    if (utm === "godoweb_ma" && !platform) {
      setUtmMediumValue(utm);
      setMorocco(true);
      formData.step1.utmMedium = utm;
      const filterCountry = countriesData.filter(
        (country) => country.name === "MOROCCO"
      );
      setCountriesList(filterCountry);
    }

    if (
      platform === "MT5" &&
      utm === "godoweb_ma" &&
      entityParam === "FSA_MA"
    ) {
      setPlatForm("MT5");
      setUtmMediumValue(utm);
      setMorocco(true);
      formData.step1.platform = "MT5";
      formData.step1.utmMedium = utm;
      setFilteredAccountTypes(filterAccountTypes("MT5"));
      const filterCountry = countriesData.filter(
        (country) => country.name === "MOROCCO"
      );
      setCountriesList(filterCountry);
    }

    // Set the value to the state
    if (refParam) {
      setRefValue(refParam);
    }

    if (refAddParam) {
      setReferrar(refAddParam);

      if (refAddParam === "98724816") {
        setFilteredAccountTypes(filterAccountTypes("MT4"));
        formData.step1.platform = "MT4";
      }
    }
    if (agreementParam) {
      setAgreementId(agreementParam);
    }

    if (ptrParam) {
      setPtrValue(ptrParam);
    }

    // clientService.authorization().then((res) => {
    //     const token = res.token;
    //     const decryptToken = decryptData(token);
    //     TokenService.setUser(decryptToken);
    // }).catch((err) => {
    //     console.log('Error for auth is ', err);
    //     toast.error("Authorization error!");
    // });
    TokenService.setUser(process.env.REACT_APP_TOKEN);
  }, [morocco, utmMediumValue, ipInfo]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedEmail(email); // Set the debounced email after delay
    }, 500);

    return () => {
      clearTimeout(handler); // Clear the timeout if the user keeps typing
    };
  }, [email]);

  // Call the API when the debounced email changes (after typing stops)
  useEffect(() => {
    const checkEmailExistence = async () => {
      if (debouncedEmail) {
        try {
          const res = await clientService.emailExist(debouncedEmail);
          if (res.agentId) {
            email.type === "email"
              ? setErrorMessage(true)
              : setPhoneError(true);
          } else {
            email.type === "email"
              ? setErrorMessage(false)
              : setPhoneError(false);
          }
        } catch (error) {
          console.error("Error checking email:", error);
        }
      }
    };

    checkEmailExistence();
  }, [debouncedEmail]);

  useEffect(() => {
    // Ensure 'ptr' is present on initial load
    if (initialLoad.current) {
      initialLoad.current = false;
      const searchParams = new URLSearchParams(location.search);

      // Check for required search parameters
      if (
        !searchParams.has("ptr") ||
        !searchParams.has("rm") ||
        !searchParams.has("nationality")
      ) {
        const newUrl = `${location.pathname}?${searchParams.toString()}`;
        navigate(newUrl, { replace: true });
      } else if (
        searchParams.has("nationality") &&
        searchParams.get("nationality") === "MOROCCO"
      ) {
        // Redirect directly to the Moroccan URL without localhost prefix
        const newUrl = `https://signup-ma.godocm.com/?${searchParams.toString()}`;
        window.location.href = newUrl;
      }
    }
  }, [location, navigate]);

  const reloadHandler = (country) => {
    // Get current search params
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("nationality", country);

    // Construct the new URL
    let newUrl;
    if (country === "MOROCCO") {
      newUrl = `https://signup-ma.godocm.com/?${searchParams.toString()}`;
      window.location.href = newUrl;
    } else {
      newUrl = `${location.pathname}?${searchParams.toString()}`;
      // Push the new URL to the history
      navigate(newUrl);
    }
  };

  const [formData, setFormData] = useState({
    step1: {
      agentId: refValue,
      parentId: ptrValue,
      entity: entityValue ? entityValue : "FSA",
      callStatus: "New Lead",
      firstName: "",
      email: "",
      phone: "",
      accountType: "",
      accountTypeCurrency: "",
      password: "",
      confirmPassword: "",
      country: "",
      countryCode: "",
      platform: "",
      utmMedium: "",
      couponeCode: ""
    },
    step2: {
      nationality: "",
      nationalityCode: "",
      otherCountryNational: NOT_APPLICABLE_VALUE,
      state: "",
      address: "",
      proofOfAddress: "",
      emiratesId: "",
      emiratesIdBack: "",
      idType: "",
      idTypeCode: "",
      passportIdNo: "",
      year: "",
      month: "",
      day: "",
      proofOfIdentity: "",
      proofOfIdentityBack: "",
      residency: "",
      residencyCode: "",
      resProofOfAddress: "",
      annualIncome: "",
      netWorth: "",
      modeOfPayment: "",
      modeOfPaymentCode: "",
      employmentStatus: "",
      employmentStatusCode: "",
      sourceOfFunds: "",
      sourceOfFundsCode: "",
      employerBusinessName: "",
      industrySector: "",
      profession: "",
      sourceOfIncome: "",
      taxResidency: "",
      otherCountryResidence: "",
      taxIdentificationNumber: "",
      specifyProfession: "",
    },
    step3: {
      experiencedTrading: false,
      sharesFrequency: "",
      cfdFrequency: "",
      powerOfAttorney: "",
      learn: true,
      accountTradedByOthers: true,
      acceptTradingTerms: false,
    },
    step4: {
      usCitizen: false,
      acknowledge: false,
      politicallyExposed: false,
      taxLiableOutsideUae: false,
      beneficialOwnerCode: "Y",
      infoCorrect: false,
      submitConsent: false,
      marketingEmail: false,
      marketingSms: false,
      marketingPhone: false,
      declaration: false,
    },
    // Add more steps as needed
  });

  const data = {
    agentId: refValue || "33",
    parentId: ptrValue || formData.step1.parentId,
    entity: entityValue ? entityValue : "FSA",
    firstName: formData.step1.firstName,
    email: formData.step1.email,
    platform: formData.step1.platform,
    accountType: formData.step1.accountType,
    accountTypeCurrency: formData.step1.accountTypeCurrency,
    country: formData.step1.country,
    countryCode: formData.step1.countryCode,
    nationality: formData.step2.nationality,
    nationalityCode: formData.step2.nationalityCode,
    otherCountryNational:
      formData.step2.otherCountryNational === NOT_APPLICABLE_VALUE
        ? ""
        : formData.step2.otherCountryNational || "",
    phone: formData.step1.phone,
    ...utmArray,
    agreementId: agreementId,
    couponeCode: formData.step1.couponeCode,

    proofOfIdentity: formData.step2.proofOfIdentity,
    proofOfIdentityBack: formData.step2.proofOfIdentityBack,
    residency: formData.step2.residency,
    residencyCode: formData.step2.residencyCode,
    resProofOfAddress: formData.step2.resProofOfAddress,
    employmentStatus: formData.step2.employmentStatus,
    sourceOfFunds: formData.step2.sourceOfFunds,
    modeOfPayment: formData.step2.modeOfPayment,
    modeOfPaymentCode: formData.step2.modeOfPaymentCode,
    employerBusinessName: formData.step2.employerBusinessName,
    jobTitle: formData.step2.industrySector,
    employer: formData.step2.employerBusinessName,
    profession: formData.step2.profession,
    specifyProfession:
      formData.step2.profession === PROFESSION_OTHER_LABEL
        ? formData.step2.specifyProfession?.trim() || ""
        : "",
    taxIdentificationNumber: formData.step2.taxIdentificationNumber,
    taxLiableUAE: !!formData.step4.taxLiableOutsideUae,
    taxResidency: formData.step4.taxLiableOutsideUae
      ? formData.step2.taxResidency
      : "",
    otherCountryResidence: formData.step4.taxLiableOutsideUae
      ? formData.step2.otherCountryResidence
      : "",
    politicallyExposed: formData.step4.politicallyExposed,
    beneficialOwnerCode: formData.step4.beneficialOwnerCode || "N",
    accountTradedByOthers:
      formData.step1.country === RESIDENCE_COUNTRY_NAME
        ? formData.step4.beneficialOwnerCode === "Y"
        : formData.step3.accountTradedByOthers === true,
    acceptTradingTerms:
      formData.step1.country === RESIDENCE_COUNTRY_NAME
        ? formData.step4.submitConsent
        : formData.step3.acceptTradingTerms,
    usCitizen: formData.step4.usCitizen,
    declaration:
      formData.step1.country === RESIDENCE_COUNTRY_NAME
        ? formData.step4.infoCorrect && formData.step4.submitConsent
        : formData.step4.declaration,
    marketingEmail: formData.step4.marketingEmail,
    marketingSms: formData.step4.marketingSms,
    marketingPhone: formData.step4.marketingPhone,

    accountStatus: "New",
    isLead: true,
    isClient: false,
  };

  const [validationErrors, setValidationErrors] = useState({
    step1: {
      firstName: false,
      phone: false,
      accountType: false,
      otpValue: false,
      country: false,
      countryCode: false,
      password: false,
      confirmPassword: false,
      platform: false,
    },
    step2: {
      nationality: false,
      nationalityCode: false,
      address: false,
      proofOfAddress: false,
      emiratesId: false,
      emiratesIdBack: false,
      idType: false,
      idTypeCode: false,
      passportIdNo: false,
      dob: false,
      day: false,
      month: false,
      year: false,
      proofOfIdentity: false,
      proofOfIdentityBack: false,
      residency: false,
      residencyCode: false,
      resProofOfAddress: false,
      annualIncome: false,
      netWorth: false,
      modeOfPayment: false,
      modeOfPaymentCode: false,
      employmentStatus: false,
      employmentStatusCode: false,
      sourceOfFunds: false,
      sourceOfFundsCode: false,
      employerBusinessName: false,
      industrySector: false,
      profession: false,
      source_Of_Income: false,
      taxResidency: false,
      taxIdentificationNumber: false,
      specifyProfession: false,
    },
    step3: {
      experiencedTrading: false,
      sharesFrequency: false,
      cfdFrequency: false,
      learn: false,
      accountTradedByOthers: false,
      powerOfAttorney: false,
      acceptTradingTerms: false,
    },
    step4: {
      usCitizen: false,
      acknowledge: false,
      politicallyExposed: false,
      taxLiableOutsideUae: false,
      beneficialOwnerCode: false,
      infoCorrect: false,
      submitConsent: false,
      declaration: false,
    },
    // other steps...
  });

  const formValueChange = (step, name, value) => {
    // Regular expression for English letters and basic punctuation
    const englishRegex = /^[a-zA-Z0-9\s.,'?!@#$%^&:*()_+=-]*$/;
    const uploadedFileNames = [
      "proofOfIdentity",
      "proofOfIdentityBack",
      "resProofOfAddress",
      "sourceOfIncome",
      "emiratesId",
      "emiratesIdBack",
    ];

    if (name === "email") {
      setEmail({ type: "email", value: value });
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(value)) {
        setEmailError(1); // Set email error state if the email is invalid
      } else {
        setEmailError(0); // Clear email error state if the email is valid
      }
    }

    if (name === "phone") {
      const phoneNumber = value.replace(/\s+/g, "");
      setEmail({ type: "phone", value: phoneNumber });
      setFormData((prevFormData) => ({
        ...prevFormData,
        [step]: {
          ...prevFormData[step],
          [name]: value.replace(/[^\d]/g, ""),
        },
      }));
    } else if (FREE_TEXT_FIELDS.has(name) || englishRegex.test(value)) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [step]: {
          ...prevFormData[step],
          [name]: value,
        },
      }));
    } else if (uploadedFileNames.includes(name)) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [step]: {
          ...prevFormData[step],
          [name]: value,
        },
      }));
    } else if (
      DROPDOWN_VALUE_FIELDS.has(name) ||
      typeof value === "boolean"
    ) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [step]: {
          ...prevFormData[step],
          [name]: value,
        },
      }));
    }
  };

  const setTaxLiableOutsideUae = (value) => {
    setFormData((prev) => ({
      ...prev,
      step4: { ...prev.step4, taxLiableOutsideUae: value },
      step2: value
        ? prev.step2
        : {
          ...prev.step2,
          taxResidency: "",
          otherCountryResidence: "",
        },
    }));
  };

  const setTaxResidencyCountry = (countryName) => {
    setFormData((prev) => ({
      ...prev,
      step2: {
        ...prev.step2,
        taxResidency: countryName,
        otherCountryResidence: countryName,
      },
    }));
  };

  const setBeneficialOwner = (isOwner) => {
    setFormData((prev) => ({
      ...prev,
      step4: {
        ...prev.step4,
        beneficialOwnerCode: isOwner ? "Y" : "N",
      },
    }));
  };

  const setAccountTradedByOthers = (isOwner) => {
    setFormData((prev) => ({
      ...prev,
      step3: {
        ...prev.step3,
        accountTradedByOthers: isOwner,
        powerOfAttorney: "",
      },
    }));
    setImageList((prev) =>
      prev.filter((item) => item.key !== "POWER_OF_ATTORNEY")
    );
  };

  const setEmploymentStatus = (value) => {
    setFormData((prev) => ({
      ...prev,
      step2: {
        ...prev.step2,
        employmentStatus: value,
        employmentStatusCode: value ? employmentStatusCodes[value] || "" : "",
      },
    }));
  };

  const setSourceOfFunds = (value) => {
    setFormData((prev) => ({
      ...prev,
      step2: {
        ...prev.step2,
        sourceOfFunds: value,
        sourceOfFundsCode: value ? uaeSourceOfFundsCodes[value] || "" : "",
      },
    }));
  };

  const setModeOfPayment = (value) => {
    setFormData((prev) => ({
      ...prev,
      step2: {
        ...prev.step2,
        modeOfPayment: value,
        modeOfPaymentCode: value ? MODE_OF_PAYMENT_CODES[value] || "" : "",
      },
    }));
  };

  const setIndustrySector = (value) => {
    setFormData((prev) => ({
      ...prev,
      step2: {
        ...prev.step2,
        industrySector: value,
        profession: "",
        specifyProfession: "",
      },
    }));
  };

  const setProfession = (value) => {
    setFormData((prev) => ({
      ...prev,
      step2: {
        ...prev.step2,
        profession: value,
        specifyProfession:
          value === PROFESSION_OTHER_LABEL ? prev.step2.specifyProfession : "",
      },
    }));
  };

  const setDefaultProfession = (value) => {
    setFormData((prev) => ({
      ...prev,
      step2: {
        ...prev.step2,
        profession: value,
      },
    }));
  };

  // const combineValidation = () => {
  //   const { firstName, email, phone, platform, accountType, country } =
  //     formData.step1;
  //   const {
  //     proofOfIdentity,
  //     proofOfIdentityBack,
  //     sourceOfFunds,
  //     resProofOfAddress,
  //     taxIdentificationNumber,
  //     emiratesId,
  //   } = formData.step2;
  //   const { acceptTradingTerms, accountTradedByOthers } = formData.step3;
  //   const { usCitizen, acknowledge } = formData.step4;

  //   // Check each field individually and set their validity
  //   const isfirstNameValid = firstName.trim() !== "";
  //   const isEmailValid = email.trim() !== "";
  //   const isPhoneValid = phone.trim() !== "";
  //   const isPlatform = platform !== "";
  //   const isAccountTypeValid = accountType !== "";
  //   const isCountryValid = country !== "";
  //   const isSourceOfFunds = sourceOfFunds;
  //   const isProofOfIdentity = proofOfIdentity !== "";
  //   const isAcceptTradingTerms = acceptTradingTerms !== false;
  //   const isAccountTradedByOthers = accountTradedByOthers !== false;
  //   const isTaxIdentificationNumber = taxIdentificationNumber !== "";
  //   const isAcknowledge = acknowledge !== false;
  //   const isEmiratesId = emiratesId !== "";

  //   const resitrictedCountriesCountried = ['TURKEY', 'EGYPT', 'IRAQ', 'JORDAN'];
  //   const isNotAllowed = resitrictedCountriesCountried.includes(formData?.step1?.country);

  //   let documentUpload = isNotAllowed ? false : true;
  //   const uploadedFiles = [
  //     proofOfIdentity,
  //     proofOfIdentityBack,
  //     resProofOfAddress,
  //     emiratesId,
  //   ];

  //   if (!isNotAllowed && proofOfIdentity === "" && resProofOfAddress === "") {
  //     toast.error(`Please Upload the requested files doc`);
  //   }

  //   if (selectedCountryCode === "ae" && !emiratesId) {
  //     toast.error(`Please Upload Emirated Id`);
  //   }

  //   if (proofOfIdentity !== "") {
  //     documentUpload = true;
  //     for (let i = 0; i < uploadedFiles.length; i++) {
  //       for (let j = i + 1; j < uploadedFiles.length; j++) {
  //         if (uploadedFiles[i] && uploadedFiles[i] === uploadedFiles[j]) {
  //           toast.error(`Uploaded files could not be the same`);
  //           return (documentUpload = false);
  //         }
  //       }
  //     }
  //   }

  //   // Set the validation errors dynamically for each field
  //   setValidationErrors((prevErrors) => ({
  //     ...prevErrors,
  //     step1: {
  //       ...prevErrors.step1,
  //       firstName: !isfirstNameValid,
  //       email: !isEmailValid,
  //       phone: !isPhoneValid,
  //       platform: !isPlatform,
  //       accountType: !isAccountTypeValid,
  //       country: !isCountryValid,
  //     },
  //     step2: {
  //       ...prevErrors.step2,
  //       sourceOfFunds: !isSourceOfFunds,
  //       proofOfIdentity: !isProofOfIdentity,
  //       resProofOfAddress: !resProofOfAddress,
  //       taxIdentificationNumber: usCitizen && !isTaxIdentificationNumber,
  //       emiratesId: selectedCountryCode === "ae" && !isEmiratesId,
  //     },
  //     step3: {
  //       ...prevErrors.step3,
  //       acceptTradingTerms: !isAcceptTradingTerms,
  //       accountTradedByOthers: !isAccountTradedByOthers,
  //     },
  //     step4: {
  //       ...prevErrors.step4,
  //       acknowledge: selectedCountryCode === "ae" && !isAcknowledge,
  //     },
  //   }));

  //   // Return true if all fields are valid, false otherwise
  //   const validTrue =
  //     isfirstNameValid &&
  //     isEmailValid &&
  //     isPhoneValid &&
  //     isAccountTypeValid &&
  //     isCountryValid &&
  //     documentUpload &&
  //     isSourceOfFunds &&
  //     isProofOfIdentity &&
  //     isAcceptTradingTerms &&
  //     isAccountTradedByOthers &&
  //     (usCitizen ? isTaxIdentificationNumber : !isTaxIdentificationNumber) &&
  //     (selectedCountryCode !== "ae" ? !isAcknowledge : isAcknowledge && isEmiratesId);

  //   if (!validTrue) {
  //     console.log(
  //       "validation: ",
  //       validTrue,
  //       selectedCountryCode !== "ae" ? !isAcknowledge : isAcknowledge
  //     );
  //     toast.error("Please fill all the fields!");
  //     return false;
  //   }
  //   return true;
  // };

  const combineValidation = () => {
    const { step1, step2, step3, step4 } = formData;
    const {
      firstName = "",
      email = "",
      phone = "",
      platform = "",
      accountType = "",
      country = "",
    } = step1;

    const {
      proofOfIdentity = "",
      proofOfIdentityBack = "",
      employmentStatus = "",
      sourceOfFunds = "",
      resProofOfAddress = "",
      taxIdentificationNumber = "",
      taxResidency = "",
      emiratesId = "",
      emiratesIdBack = "",
      industrySector = "",
      profession = "",
      employerBusinessName = "",
      specifyProfession = "",
    } = step2;

    const {
      acceptTradingTerms = false,
      accountTradedByOthers = null,
    } = step3;
    const {
      usCitizen = false,
      taxLiableOutsideUae = false,
      infoCorrect = false,
      submitConsent = false,
    } = step4;

    const restrictedCountries = ["TURKEY", "EGYPT", "IRAQ", "JORDAN"];
    const isRestricted = restrictedCountries.includes(country);
    const isUAEResidence = country === RESIDENCE_COUNTRY_NAME;
    const isUAE = selectedCountryCode === "ae" || isUAEResidence;
    const hasBeneficialOwnerAnswer =
      accountTradedByOthers === true || accountTradedByOthers === false;

    // Base validations
    const validations = {
      firstName: firstName.trim() !== "",
      email: email.trim() !== "",
      phone: phone.trim() !== "",
      platform: platform.trim() !== "",
      accountType: accountType.trim() !== "",
      country: country.trim() !== "",
      employmentStatus:
        !isUAEResidence || employmentStatus.trim() !== "",
      sourceOfFunds: sourceOfFunds.trim() !== "",
      industrySector: !isUAEResidence || industrySector.trim() !== "",
      profession: profession.trim() !== "",
      employerBusinessName:
        !isUAEResidence || employerBusinessName.trim() !== "",
      specifyProfession:
        !isUAEResidence ||
        profession !== PROFESSION_OTHER_LABEL ||
        !!specifyProfession?.trim(),
      proofOfIdentity: isUAE
        ? true
        : !isRestricted
          ? !!proofOfIdentity
          : true,
      resProofOfAddress: !isRestricted ? !!resProofOfAddress : true,
      emiratesId: isUAE ? !!emiratesId : true,
      emiratesIdBack: isUAE ? !!emiratesIdBack : true,
      taxIdentificationNumber: usCitizen ? !!taxIdentificationNumber : true,
      taxResidency:
        isUAEResidence && taxLiableOutsideUae ? !!taxResidency : true,
      infoCorrect: isUAEResidence ? !!infoCorrect : true,
      submitConsent: isUAEResidence ? !!submitConsent : true,
      acceptTradingTerms: isUAEResidence ? true : !!acceptTradingTerms,
      accountTradedByOthers: isUAEResidence ? true : hasBeneficialOwnerAnswer,
    };

    // Handle document uploads
    // const uploadedFiles = [proofOfIdentity, proofOfIdentityBack, resProofOfAddress, emiratesId];

    if (!country.trim()) {
      toast.error("Please select your country of residence.");
      validations.country = false;
    }

    if (!isRestricted && !isUAE && !proofOfIdentity && !resProofOfAddress) {
      toast.error("Please upload the requested documents.");
      validations.proofOfIdentity = false;
      validations.resProofOfAddress = false;
    }

    if (isUAE && (!emiratesId || !emiratesIdBack)) {
      toast.error("Please upload Emirates ID (front and back).");
      if (!emiratesId) validations.emiratesId = false;
      if (!emiratesIdBack) validations.emiratesIdBack = false;
    }

    if (
      !isUAEResidence &&
      !hasBeneficialOwnerAnswer
    ) {
      toast.error(t("BeneficialOwnerRequired"));
    }

    if (
      isUAEResidence &&
      profession === PROFESSION_OTHER_LABEL &&
      !specifyProfession?.trim()
    ) {
      toast.error(t("SpecifyProfessionRequired"));
      validations.specifyProfession = false;
    }

    if (
      isUAEResidence &&
      (!validations.industrySector ||
        !validations.profession ||
        !validations.employmentStatus ||
        !validations.sourceOfFunds ||
        !validations.employerBusinessName)
    ) {
      toast.error(t("UaeEmploymentDetailsRequired"));
    }

    // Duplicate file check
    // const uniqueFiles = new Set(uploadedFiles.filter(Boolean));
    // if (uniqueFiles.size !== uploadedFiles.filter(Boolean).length) {
    //   toast.error("Uploaded files cannot be the same.");
    //   return false;
    // }

    // Update field-level errors
    setValidationErrors({
      step1: {
        firstName: !validations.firstName,
        email: !validations.email,
        phone: !validations.phone,
        platform: !validations.platform,
        accountType: !validations.accountType,
        country: !validations.country,
      },
      step2: {
        employmentStatus:
          isUAEResidence && !validations.employmentStatus,
        sourceOfFunds: !validations.sourceOfFunds,
        industrySector: isUAEResidence && !validations.industrySector,
        profession: !validations.profession,
        employerBusinessName:
          isUAEResidence && !validations.employerBusinessName,
        proofOfIdentity: !validations.proofOfIdentity,
        resProofOfAddress: !validations.resProofOfAddress,
        taxIdentificationNumber: !validations.taxIdentificationNumber,
        taxResidency: !validations.taxResidency,
        emiratesId: !validations.emiratesId,
        emiratesIdBack: !validations.emiratesIdBack,
        specifyProfession:
          isUAEResidence && !validations.specifyProfession,
      },
      step3: {
        acceptTradingTerms: !validations.acceptTradingTerms,
        accountTradedByOthers:
          !isUAEResidence && !validations.accountTradedByOthers,
      },
      step4: {
        infoCorrect: !validations.infoCorrect,
        submitConsent: !validations.submitConsent,
      },
    });

    // Check final result
    const allValid = Object.values(validations).every(Boolean);

    if (!allValid) {
      toast.error("Please fill all the required fields correctly.");
      console.log("Validation failed: ", validations, isRestricted, country);
      return false;
    }

    return true;
  };

  const verifyFilesAccessible = async (imageList) => {
    if (!imageList || imageList.length === 0) return true;

    for (const item of imageList) {
      const file = item?.value?.[0];
      if (!file) continue;

      try {
        await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(true);
          reader.onerror = () => reject(new Error(`File not accessible: ${file.name}`));
          reader.readAsArrayBuffer(file); // Try reading bytes to ensure access
        });
      } catch (err) {
        console.error(`File check failed: ${file.name}`, err);
        toast.error(`"${file.name}" not accessible. Please reselect the file.`);
        return false;
      }
    }

    return true;
  };


  const submitForm = async (e) => {
    e.preventDefault();

    if (errorMessage) {
      toast.error("Email Exist!");
    }

    if (phoneError) {
      toast.error("Phone Exist!");
    }

    if (combineValidation() && !errorMessage && !phoneError) {

      const isFilesValid = await verifyFilesAccessible(imageList);
      if (!isFilesValid) {
        console.warn("File accessibility check failed — skipping lead creation.");
        return;
      }

      setLoading(true);
      data.acceptedTerms = true;
      data.acceptedRisk = true;
      data.acceptedPrivacy = true;
      data.acceptedExecutionPolicy = true;
      data.acceptedConflicts = true;
      data.phone =
        phoneNumber !== undefined ? phoneNumber + data.phone : data.phone;
      data.emailType = "emailConfirmationGodoCapital";

      const postClient = await clientService.postLead(data);

      if (postClient && !postClient.data && postClient.length !== 0) {
        const formDataValue = new FormData();
        formDataValue.append("customerId", postClient.id);

        imageList.forEach((item) => {
          const file = item.value[0];
          if (file && file instanceof File) {
            formDataValue.append(item.key, file);
          }
        });

        setSubmittedData(postClient);

        const logData = {
          type: "userRegister",
          customerId: postClient && postClient.id,
          note: {
            note:
              ipInfo ?
                JSON.stringify(ipInfo) + ` urlLink=${window.location.href}` : `urlLink=${window.location.href}`,
          },
        };

        activitiesServices.postActivity(logData);

        try {
          if (imageList.length !== 0) {
            await clientService.uploadDocuments(formDataValue);
          }
        } catch (error) {
          console.error('Upload error:', error.message);
          if (error.message.includes("ERR_FILE_NOT_FOUND")) {
            toast.error("File not found. Please reselect the document and try again.");
          } else {
            toast.error("Network error during document upload.");
          }
          setSubmittedData("");
          setLoading(false);
          return; // stop further actions
        }

        setLoading(false);
        toast.success("Successfully Registered!");
      } else {
        console.log("error: ", postClient);
        setLoading(false);
        toast.error(postClient.data.message);
      }
    }
  };

  const updateListFunction = (key, files) => {
    // Check if the key already exists in the imageList
    const existingIndex = imageList.findIndex((entry) => entry.key === key);

    if (existingIndex !== -1) {
      // If the key already exists, update its value
      const updatedList = [...imageList];
      updatedList[existingIndex].value = files;

      setImageList(updatedList);
      console.log("file: ", imageList);
    } else {
      // If the key does not exist, add it as a new entry
      const newValue = {
        key,
        value: files,
      };

      const updatedList = [...imageList, newValue];
      setImageList(updatedList);
      console.log("file: ", imageList);
    }
  };

  const getUploadedFileLabel = (value) => {
    if (!value) return "Choose a file";
    const name = value.split("\\").pop() || value;
    return name.slice(0, 13);
  };

  const handleSingleDocUpload = (e, fieldName, imageKey) => {
    const maxFileSize = 5 * 1024 * 1024;
    const allowedExtensions = ["jpg", "jpeg", "png", "pdf", "heic"];
    const file = e.target.files?.[0];

    if (!file) {
      formValueChange("step2", fieldName, "");
      setImageList((prev) => prev.filter((item) => item.key !== imageKey));
      return;
    }

    const ext = file.name.split(".").pop()?.toLowerCase();
    if (file.size > maxFileSize) {
      alert("File size exceeds the limit of 5 MB.");
      e.target.value = "";
      formValueChange("step2", fieldName, "");
      return;
    }
    if (!allowedExtensions.includes(ext)) {
      alert("Please Upload files with extension jpg, jpeg, png, pdf, heic");
      e.target.value = "";
      formValueChange("step2", fieldName, "");
      return;
    }

    setFileData(file.name);
    updateListFunction(imageKey, [file]);
    formValueChange("step2", fieldName, file.name);
  };

  const filterAccountTypes = (platform) => {
    if ((formData.step1.country === 'EGYPT' || formData.step1.country === 'TURKEY') && data.agentId === '33') {
      return jsonData.filter(
        (item) => platform === 'MT5' ?
          item["TP platform"] === platform && item["Server Number"] === 6 :
          item["TP platform"] === platform && item["Server Number"] === 1
      );
    }

    if (ptrValue && refValue !== "38" && !morocco) {
      return jsonData.filter(
        (item) => item["TP platform"] === platform && item["ib"] === 1
      );
    } else if (ptrValue && refValue === "38" && !morocco) {
      return jsonData.filter(
        (item) => item["TP platform"] === platform && item["ib"] === 1
      );
    } else if (ptrValue && refValue !== "38" && morocco) {
      return jsonData.filter(
        (item) => item["TP platform"] === platform && item["ib"] === 3
      );
    } else if (!ptrValue && refValue === "38") {
      return jsonData.filter(
        (item) =>
          item["TP platform"] === platform && item["Server Number"] === 2
      );
    } else if (morocco) {
      return jsonData.filter(
        (item) =>
          item["TP platform"] === platform && item["Server Number"] === 3
      );
    } else if (referrar === "98724816") {
      return jsonData.filter(
        (item) =>
          item["TP platform"] === platform && item["Server Number"] === 1
      );
    } else {
      return jsonData.filter(
        (item) =>
          item["TP platform"] === platform && item["Server Number"] === 1
      );
    }
  };

  const accountTypeValue = (platform) => {
    // Determine accountType based on platform
    const accountType =
      morocco === false
        ? platform === "MT5"
          ? "2"
          : "14"
        : platform === "MT5"
          ? "42"
          : "38";

    // Update form data
    setFormData((prevFormData) => ({
      ...prevFormData,
      step1: {
        ...prevFormData.step1,
        accountType,
        accountTypeCurrency: "USD",
      },
    }));

    formValueChange("step1", "accountType", '');
  };

  const handleChange = (selectedOption) => {
    const country = selectedOption ? selectedOption.value : null;

    if (formData.step1.platform || formData.step1.accountType) {
      formData.step1.platform = '';
      formData.step1.accountType = '';
    }

    if (country) {
      const selectedCountryData = countriesData.find(
        (item) => item.name === country
      );
      if (selectedCountryData) {
        if (selectedCountryData.name === "MOROCCO") {
          reloadHandler("MOROCCO");
        }

        setSelectedCountryCode(selectedCountryData.iso2.toLowerCase());
        formValueChange("step1", "country", selectedCountryData.name);
        formValueChange("step1", "countryCode", selectedCountryData.iso2);
        formValueChange("step2", "residency", selectedCountryData.name);
        formValueChange("step2", "residencyCode", selectedCountryData.iso2);

        const isUAE = selectedCountryData.name === RESIDENCE_COUNTRY_NAME;
        if (isUAE) {
          setCopponCode(false);
          setImageList((prev) =>
            prev.filter(
              (item) =>
                item.key !== "PROOF_OF_ID" &&
                item.key !== "PROOF_OF_ID_BACK" &&
                item.key !== "POWER_OF_ATTORNEY"
            )
          );
        } else {
          setImageList((prev) =>
            prev.filter(
              (item) =>
                item.key !== "EMIRATES_ID" && item.key !== "EMIRATES_ID_BACK"
            )
          );
        }
        const useBankPaymentDefault = isBankPaymentDefaultCountry(
          selectedCountryData.name
        );
        setFormData((prev) => {
          const legalReset = isUAE
            ? resetUaeLegalFields(prev.step2, prev.step3, prev.step4)
            : {
              step2: prev.step2,
              ...resetDefaultAgreementFields(prev.step3, prev.step4),
            };
          return {
            ...prev,
            step1: isUAE
              ? { ...prev.step1, couponeCode: "" }
              : prev.step1,
            step2: {
              ...resetEmploymentFields(legalReset.step2),
              otherCountryNational:
                legalReset.step2?.otherCountryNational || NOT_APPLICABLE_VALUE,
              modeOfPayment: useBankPaymentDefault ? BANK_MODE_OF_PAYMENT : "",
              modeOfPaymentCode: useBankPaymentDefault
                ? MODE_OF_PAYMENT_CODES[BANK_MODE_OF_PAYMENT]
                : "",
              ...(isUAE
                ? { proofOfIdentity: "", proofOfIdentityBack: "" }
                : { emiratesId: "", emiratesIdBack: "" }),
            },
            step3: legalReset.step3,
            step4: legalReset.step4,
          };
        });
      }
    } else {
      setFormData((prev) => {
        const legalReset = resetDefaultAgreementFields(prev.step3, prev.step4);
        return {
          ...prev,
          step1: { ...prev.step1, country: "", countryCode: "" },
          step2: {
            ...resetEmploymentFields(prev.step2),
            otherCountryNational:
              prev.step2.otherCountryNational || NOT_APPLICABLE_VALUE,
          },
          step3: legalReset.step3,
          step4: legalReset.step4,
        };
      });
    }
  };

  const isUAEResidence =
    formData.step1.country === RESIDENCE_COUNTRY_NAME;
  const isBankPaymentDefaultResidence = isBankPaymentDefaultCountry(
    formData.step1.country
  );
  const uaeEmploymentFieldsMd =
    formData.step2.profession === PROFESSION_OTHER_LABEL ? 6 : 4;

  useEffect(() => {
    if (
      isBankPaymentDefaultResidence &&
      formData.step2.modeOfPayment !== BANK_MODE_OF_PAYMENT
    ) {
      setModeOfPayment(BANK_MODE_OF_PAYMENT);
    }
  }, [isBankPaymentDefaultResidence, formData.step2.modeOfPayment]);

  const taxResidencyCountryOptions = useMemo(
    () =>
      getTaxResidencyCountries().map((item) => ({
        value: item.name,
        label: item.name,
      })),
    []
  );

  const uaeProfessionOptions =
    formData.step2.industrySector &&
      industrySectorProfessions[formData.step2.industrySector]
      ? industrySectorProfessions[formData.step2.industrySector]
      : [];

  const countryOptions = countryList
    .filter((item) => !FSC_RESTRICTED_COUNTRIES.has(item.name))
    .map((item) => ({
      value: item.name,
      label: item.name,
    }));

  const nationalityCountryOptions = useMemo(
    () =>
      getNationalityCountries(entityValue || "FSA").map((item) => ({
        value: item.name,
        label: item.name,
      })),
    [entityValue]
  );

  const dualNationalityOptions = useMemo(
    () => [
      { value: NOT_APPLICABLE_VALUE, label: t("NotApplicable") },
      ...nationalityCountryOptions,
    ],
    [nationalityCountryOptions, t]
  );

  const setNationalityFields = (field, selected) => {
    if (!selected) {
      setFormData((prev) => ({
        ...prev,
        step2: {
          ...prev.step2,
          ...(field === "nationality"
            ? { nationality: "", nationalityCode: "" }
            : { otherCountryNational: NOT_APPLICABLE_VALUE }),
        },
      }));
      return;
    }

    const value = selected.value;
    if (field === "nationality") {
      const country = countriesData.find((item) => item.name === value);
      setFormData((prev) => ({
        ...prev,
        step2: {
          ...prev.step2,
          nationality: value,
          nationalityCode: country?.iso2 || "",
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        step2: {
          ...prev.step2,
          otherCountryNational: value || NOT_APPLICABLE_VALUE,
        },
      }));
    }
  };

  const isRtl =
    i18n.language === "ar" ||
    i18n.language === "ir" ||
    i18n.language === "fa";

  const legalPdfLink = (href, children) => (
    <a
      href={href}
      className="morocco-link"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );

  const AgreementCheckboxRow = ({
    id,
    checked,
    onChange,
    invalid,
    required,
    children,
  }) => (
    <Col
      md={12}
      className={`agreement-checkbox-row ${invalid ? "text-danger" : "text-black"
        }`}
    >
      <div
        className={`d-flex align-items-start gap-2 ${isRtl ? "flex-row-reverse text-end" : ""
          }`}
      >
        <Input
          className="form-check-input agreement-checkbox-input mt-1"
          type="checkbox"
          id={id}
          checked={checked}
          onChange={onChange}
        />
        <Label className="agreement-checkbox-label mb-0" htmlFor={id}>
          {required && <span className="agreement-required">* </span>}
          {children}
        </Label>
      </div>
    </Col>
  );

  const LegalYesNoField = ({
    label,
    value,
    onYes,
    onNo,
    fullWidth = false,
    invalid = false,
    required = false,
  }) => (
    <FormGroup className={`legal-field ${invalid ? "legal-field--invalid" : ""}`}>
      <Label className="legal-field__label">
        {required && <span className="agreement-required">* </span>}
        {label}
      </Label>
      <div
        className={`yes-no-toggle ${fullWidth ? "yes-no-toggle--full" : ""}`}
        role="group"
        aria-label={label}
      >
        <button
          type="button"
          className={value === true ? "active" : ""}
          onClick={onYes}
          aria-pressed={value === true}
        >
          {t("Yes")}
        </button>
        <button
          type="button"
          className={value === false ? "active" : ""}
          onClick={onNo}
          aria-pressed={value === false}
        >
          {t("No")}
        </button>
      </div>
    </FormGroup>
  );

  const beneficialOwnerInvalid =
    validationErrors.step3.accountTradedByOthers &&
    formData.step3.accountTradedByOthers === null;

  const BeneficialOwnerField = () => (
    <>
      <LegalYesNoField
        label={t("BeneficialOwnerQuestion")}
        value={formData.step3.accountTradedByOthers}
        onYes={() => setAccountTradedByOthers(true)}
        onNo={() => setAccountTradedByOthers(false)}
        invalid={beneficialOwnerInvalid}
        required
      />
      <p
        className={`beneficial-owner-poa-link mt-2 mb-0 ${isRtl ? "text-end" : ""
          }`}
      >
        {t("BeneficialOwnerPoaHint")}{" "}
        <a
          className="morocco-link"
          href="https://godocm.com/fr/limited-power-of-attorney-account-manager"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("PowerofAttorney")}
        </a>
      </p>
      {beneficialOwnerInvalid && (
        <span className="text-danger d-block mt-1">
          {t("BeneficialOwnerRequired")}
        </span>
      )}
    </>
  );

  const fieldControlClass = (invalid) =>
    `form-control form-field__control${invalid ? " is-invalid" : ""}`;

  const isCountryInvalid =
    validationErrors.step1.country && !formData.step1.country;

  const countrySelectComponents = {
    IndicatorSeparator: () => null,
    DropdownIndicator: (props) =>
      isCountryInvalid ? (
        <div className="country-select__invalid-icon">
          <InvalidFeedbackIcon />
        </div>
      ) : (
        <components.DropdownIndicator {...props} />
      ),
  };

  const countrySelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 42,
      borderRadius: 8,
      fontSize: "0.875rem",
      borderColor: isCountryInvalid
        ? "#dc3545"
        : state.isFocused
          ? "#094d96"
          : "#ced4da",
      boxShadow: isCountryInvalid
        ? "none"
        : state.isFocused
          ? "0 0 0 0.15rem rgba(9, 77, 150, 0.15)"
          : "none",
      "&:hover": {
        borderColor: isCountryInvalid
          ? "#dc3545"
          : state.isFocused
            ? "#094d96"
            : "#ced4da",
      },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <div
      className="formBody morocco-form"
      style={{
        fontSize:
          i18n.language === "ar" || i18n.language === "ir" ? "15px" : "",
      }}
    >
      <div id="recaptcha-container" style={{ display: "none" }}></div>{" "}
      <>
        <CustomForm>
          <div className="justify mt-2">
            <Row
              className={`${i18n.language === "ar" || i18n.language === "ir"
                ? "flex-row-reverse text-end"
                : ""
                } mb-2`}
            >
              <Col md={12} lg={6}>
                <FormGroup id="signupForm">
                  <Label>{t("FullName")}</Label>
                  <div className="input-group">
                    <Input
                      className={
                        "form-control " +
                        (!validationErrors.step1.firstName ||
                          formData.step1.firstName
                          ? ""
                          : "is-invalid")
                      }
                      name="firstName"
                      type="text"
                      required=""
                      placeholder="John"
                      value={formData.step1.firstName}
                      onChange={(e) => {
                        // Remove non-alphanumeric characters except spaces
                        let cleanedInput = e.target.value.replace(
                          /[^a-zA-Z0-9 ]/g,
                          ""
                        );

                        // Split the input into words, capitalize the first letter of each word, and join them back
                        let capitalizedInput = cleanedInput
                          .split(" ")
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1)
                          )
                          .join(" ");

                        // Update the form value
                        formValueChange("step1", "firstName", capitalizedInput);
                      }}
                    />
                  </div>
                </FormGroup>
              </Col>
              <Col md={12} lg={6}>
                <FormGroup>
                  <Label>{t("EmailAddress")}</Label>
                  <div className="input-group">
                    <Input
                      className={
                        "form-control " +
                        (!validationErrors.step1.email || formData.step1.email
                          ? ""
                          : "is-invalid")
                      }
                      type="email"
                      name="email"
                      validation={{ required: true }}
                      value={formData.step1.email}
                      placeholder="john@gmail.com"
                      invalid={emailError === 1 && formData.step1.email !== ""} // Invalid if emailError is 1 and email is not empty
                      valid={emailError === 0 && formData.step1.email !== ""} // Valid if emailError is 0 and email is not empty
                      onChange={(e) =>
                        formValueChange("step1", e.target.name, e.target.value)
                      }
                    />
                  </div>
                  {errorMessage && (
                    <span className="text-danger">Email Already Exist</span>
                  )}
                </FormGroup>
              </Col>
            </Row>
            <Row
              className={`g-3 align-items-start mb-2 ${i18n.language === "ar" || i18n.language === "ir"
                ? "flex-row-reverse text-end"
                : ""
                }`}
            >
              <Col md={12} lg={6}>
                <FormField label={t("CountryOfResidence")}>
                  <Select
                    className="country-residence-select"
                    classNamePrefix="country-select"
                    components={countrySelectComponents}
                    styles={countrySelectStyles}
                    name="country"
                    options={countryOptions}
                    value={
                      countryOptions.find(
                        (option) => option.value === formData.step1.country
                      ) || null
                    }
                    onChange={handleChange}
                    placeholder="-- Select --"
                    menuPlacement="bottom"
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                  />
                </FormField>
              </Col>
              <Col md={12} lg={6} className="phoneNum">
                <FormField label={t("PhoneNumber")}>
                  <IntlTelInput
                    name="phone"
                    containerClassName="intl-tel-input"
                    inputClassName={
                      "form-control form-field__control " +
                      (!validationErrors.step1.phone || formData.step1.phone
                        ? ""
                        : "is-invalid")
                    }
                    style={{ width: "100%" }}
                    maxLength={15}
                    format="true"
                    defaultCountry={selectedCountryCode || "ae"}
                    useMobileFullscreenDropdown={false}
                    value={formData.step1.phone}
                    onPhoneNumberChange={(
                      status,
                      value,
                      countryData,
                      number,
                      id
                    ) => {
                      let cleanValue = value;

                      if (cleanValue.startsWith("0")) {
                        cleanValue = cleanValue.substring(1);
                      }

                      setPhoneNumber(countryData.dialCode);
                      formValueChange("step1", "phone", cleanValue);
                    }}
                  />
                  {phoneError && (
                    <span className="text-danger">Phone Already Exist</span>
                  )}
                </FormField>
              </Col>
            </Row>
            <Row
              className={`mb-2 ${i18n.language === "ar" || i18n.language === "ir"
                ? "flex-row-reverse text-end"
                : ""
                }`}
            >
              <Col xs={12} className="form-section">
                <Row className="g-3 form-section__grid">
                  <Col xs={12} md={6}>
                    <FormField label={t("Nationality")}>
                      <Select
                        className="mb-0"
                        instanceId="morocco-nationality"
                        name="nationality"
                        options={nationalityCountryOptions}
                        value={
                          nationalityCountryOptions.find(
                            (option) =>
                              option.value === formData.step2.nationality
                          ) || null
                        }
                        onChange={(selected) =>
                          setNationalityFields("nationality", selected)
                        }
                        placeholder={t("SelectPlaceholder")}
                        isClearable
                      />
                    </FormField>
                  </Col>
                  <Col xs={12} md={6}>
                    <FormField label={t("DualNationality")}>
                      <Select
                        className="mb-0"
                        instanceId="morocco-dual-nationality"
                        name="otherCountryNational"
                        options={dualNationalityOptions}
                        value={
                          dualNationalityOptions.find(
                            (option) =>
                              option.value ===
                              (formData.step2.otherCountryNational ||
                                NOT_APPLICABLE_VALUE)
                          ) || dualNationalityOptions[0]
                        }
                        onChange={(selected) =>
                          setNationalityFields("dual", selected)
                        }
                        placeholder={t("SelectPlaceholder")}
                        isClearable={false}
                      />
                    </FormField>
                  </Col>
                  <Col xs={12} md={6}>
                    <FormField label={t("Platform")}>
                      <Input
                        type="select"
                        name="platform"
                        className={fieldControlClass(
                          validationErrors.step1.platform &&
                          !formData.step1.platform
                        )}
                        value={formData.step1.platform}
                        onChange={(e) => {
                          formValueChange(
                            "step1",
                            e.target.name,
                            e.target.value
                          );
                          setFilteredAccountTypes(
                            filterAccountTypes(e.target.value)
                          );
                          accountTypeValue(e.target.value);
                        }}
                      >
                        <option value="">-- Select --</option>
                        <option value="MT5">MT5</option>
                        {!ptrValue && (
                          <option value="GTN">GODO Invest</option>
                        )}
                      </Input>
                    </FormField>
                  </Col>
                  <Col xs={12} md={6}>
                    <FormField label={t("AccountType")}>
                      <Input
                        type="select"
                        name="accountType"
                        className={fieldControlClass(
                          validationErrors.step1.accountType &&
                          !formData.step1.accountType
                        )}
                        value={formData.step1.accountType}
                        onChange={(e) => {
                          if (e.target.value !== "") {
                            formValueChange(
                              "step1",
                              e.target.name,
                              e.target.value
                            );
                            const accountCurrency =
                              filteredAccountTypes.filter(
                                (account) =>
                                  account.Id === parseInt(e.target.value)
                              );
                            formValueChange(
                              "step1",
                              "accountTypeCurrency",
                              accountCurrency[0].Currency
                            );
                          }
                        }}
                      >
                        <option value="">-- Select --</option>
                        {referrar === "98724816" ? (
                          <option value="55">MANAGED</option>
                        ) : (
                          filteredAccountTypes &&
                          filteredAccountTypes.map((item, index) => (
                            <option key={index} value={item["Id"]}>
                              {item["Account type"]}
                            </option>
                          ))
                        )}
                      </Input>
                    </FormField>
                  </Col>
                  {isUAEResidence ? (
                    <>
                      <Col xs={12} md={6}>
                        <FormField label={t("IndustrySector")}>
                          <Input
                            type="select"
                            name="industrySector"
                            className={fieldControlClass(
                              validationErrors.step2.industrySector &&
                              !formData.step2.industrySector
                            )}
                            value={formData.step2.industrySector}
                            onChange={(e) =>
                              setIndustrySector(e.target.value)
                            }
                          >
                            <option value="">{t("SelectPlaceholder")}</option>
                            {industrySectorOptions.map((sector) => (
                              <option key={sector} value={sector}>
                                {stripBracketedText(sector)}
                              </option>
                            ))}
                          </Input>
                        </FormField>
                      </Col>
                      <Col xs={12} md={6}>
                        <FormField label={t("Profession")}>
                          <Input
                            type="select"
                            name="profession"
                            className={fieldControlClass(
                              validationErrors.step2.profession &&
                              !formData.step2.profession
                            )}
                            value={formData.step2.profession}
                            disabled={!formData.step2.industrySector}
                            onChange={(e) => setProfession(e.target.value)}
                          >
                            <option value="">
                              {formData.step2.industrySector
                                ? t("SelectPlaceholder")
                                : t("SelectIndustryFirst")}
                            </option>
                            {uaeProfessionOptions.map((item) => (
                              <option key={item} value={item}>
                                {stripBracketedText(item)}
                              </option>
                            ))}
                          </Input>
                        </FormField>
                      </Col>
                      {formData.step2.profession ===
                        PROFESSION_OTHER_LABEL && (
                          <Col xs={12} md={6}>
                            <FormField label={t("SpecifyProfession")}>
                              <Input
                                type="text"
                                name="specifyProfession"
                                className={`form-control form-field__control${validationErrors.step2.specifyProfession &&
                                  !formData.step2.specifyProfession?.trim()
                                  ? " is-invalid"
                                  : ""
                                  }`}
                                placeholder={t(
                                  "SpecifyProfessionPlaceholder"
                                )}
                                value={formData.step2.specifyProfession}
                                onChange={(e) =>
                                  formValueChange(
                                    "step2",
                                    "specifyProfession",
                                    e.target.value
                                  )
                                }
                              />
                            </FormField>
                          </Col>
                        )}
                      <Col xs={12} md={uaeEmploymentFieldsMd}>
                        <FormField label={t("EmploymentStatus")}>
                          <Input
                            type="select"
                            name="employmentStatus"
                            className={fieldControlClass(
                              validationErrors.step2.employmentStatus &&
                              !formData.step2.employmentStatus
                            )}
                            value={formData.step2.employmentStatus}
                            onChange={(e) =>
                              setEmploymentStatus(e.target.value)
                            }
                          >
                            <option value="">{t("SelectPlaceholder")}</option>
                            <option value="Employed (Salaried)">
                              Employed (Salaried)
                            </option>
                            <option value="Self-Employed">Self-Employed</option>
                            <option value="Business Owner/Director">
                              Business Owner/Director
                            </option>
                            <option value="Retired">Retired</option>
                            <option value="Student">Student</option>
                            <option value="Unemployed/Homemaker">
                              Unemployed/Homemaker
                            </option>
                            <option value="Other">Other</option>
                          </Input>
                        </FormField>
                      </Col>
                      <Col xs={12} md={uaeEmploymentFieldsMd}>
                        <FormField label={t("SourceOfFunds")}>
                          <Input
                            type="select"
                            name="sourceOfFunds"
                            className={fieldControlClass(
                              validationErrors.step2.sourceOfFunds &&
                              !formData.step2.sourceOfFunds
                            )}
                            value={formData.step2.sourceOfFunds}
                            onChange={(e) => setSourceOfFunds(e.target.value)}
                          >
                            <option value="">{t("SelectPlaceholder")}</option>
                            <option value="Salary">Salary</option>
                            <option value="Savings">Savings</option>
                            <option value="Pension">Pension</option>
                            <option value="Profits/dividends">
                              Profits/dividends
                            </option>
                            <option value="Inheritance & Gifts">
                              Inheritance & Gifts
                            </option>
                            <option value="Sale of Assets">Sale of Assets</option>
                            <option value="Crypto investments">
                              Crypto investments
                            </option>
                            <option value="Other">Other</option>
                          </Input>
                        </FormField>
                      </Col>
                      <Col xs={12} md={uaeEmploymentFieldsMd}>
                        <FormField label={t("EmployerBusinessName")}>
                          <Input
                            type="text"
                            name="employerBusinessName"
                            className={fieldControlClass(
                              validationErrors.step2.employerBusinessName &&
                              !formData.step2.employerBusinessName
                            )}
                            placeholder={t("EmployerBusinessNamePlaceholder")}
                            value={formData.step2.employerBusinessName}
                            onChange={(e) =>
                              formValueChange(
                                "step2",
                                "employerBusinessName",
                                e.target.value
                              )
                            }
                          />
                        </FormField>
                      </Col>
                    </>
                  ) : (
                    <>
                      <Col xs={12} md={6}>
                        <FormField label={t("Profession")}>
                          <Input
                            type="select"
                            name="profession"
                            className={fieldControlClass(
                              validationErrors.step2.profession &&
                              !formData.step2.profession
                            )}
                            value={formData.step2.profession}
                            onChange={(e) =>
                              setDefaultProfession(e.target.value)
                            }
                          >
                            <option value="">-- Select --</option>
                            <option value="Salaried">Salaried</option>
                            <option value="Self-Employed">Self-Employed</option>
                            <option value="Student">Student</option>
                            <option value="Freelancer">Freelancer</option>
                            <option value="Management">Management</option>
                            <option value="Unemployed">Unemployed</option>
                          </Input>
                        </FormField>
                      </Col>
                      <Col xs={12} md={6}>
                        <FormField label={t("SourceOfFunds")}>
                          <Input
                            type="select"
                            name="sourceOfFunds"
                            className={fieldControlClass(
                              validationErrors.step2.sourceOfFunds &&
                              !formData.step2.sourceOfFunds
                            )}
                            value={formData.step2.sourceOfFunds}
                            onChange={(e) => setSourceOfFunds(e.target.value)}
                          >
                            <option value="">{t("SelectPlaceholder")}</option>
                            <option value="Salary">Salary</option>
                            <option value="Pension">Pension</option>
                            <option value="Profits/dividends">
                              Profits/dividends
                            </option>
                            <option value="Sale of Assets">Sale of Assets</option>
                            <option value="Inheritance & Gifts">
                              Inheritance & Gifts
                            </option>
                            <option value="Savings">Savings</option>
                            <option value="Crypto investments">
                              Crypto investments
                            </option>
                            <option value="Other">Other</option>
                          </Input>
                        </FormField>
                      </Col>
                    </>
                  )}
                </Row>
              </Col>
            </Row>
            <div
              className={`z-50 d-flex flex-wrap align-items-center justify-content-center ${i18n.language === "ar" || i18n.language === "ir"
                ? "justify-content-lg-end"
                : "justify-content-lg-start"
                } fileUploadBox mb-2 ${i18n.language === "ar" || i18n.language === "ir"
                  ? "flex-row-reverse text-end"
                  : ""
                }`}
            >
              {!isUAEResidence && (
                <>
                  <div style={{ flex: "1px" }}>
                    <Label for="proofOfIdentity" className="uploadButton fw-bold">
                      {" "}
                      {t("ProofOfIdentity")}
                      <div
                        className="tooltip"
                        style={{
                          opacity: "1",
                          position: "relative",
                          top: "-1px",
                          ...(i18n.language === "ar" || i18n.language === "ir"
                            ? { left: "0" }
                            : { left: "5px" }),
                        }}
                      >
                        <img
                          src={toolTipIcon}
                          alt="infoIcon"
                          className={`${(i18n.language === "ar" || i18n.language === "ir") &&
                            "ms-2"
                            }`}
                          width={"10px"}
                        />
                        <span
                          className={` ${i18n.language === "ar" || i18n.language === "ir"
                            ? "tooltiptextArabic"
                            : "tooltiptext"
                            }`}
                        >
                          {t("POIToolTipHeading")} <b>{t("ONE")}</b>{" "}
                          {t("POIToolTipEnding")}
                          <ul>
                            <li>{t("POIToolTipList1")}</li>
                            <li>{t("POIToolTipList2")}</li>
                            <li>{t("POIToolTipList3")}</li>
                          </ul>
                        </span>
                      </div>
                      <div className="fileUploadButton">
                        <img src={uploadIcon} alt="uploadIcon" width={"40px"} />
                        <p className="m-0 text-center">
                          {getUploadedFileLabel(formData.step2.proofOfIdentity)}
                        </p>
                      </div>
                    </Label>
                    <Input
                      className={
                        "d-none mb-3 mb-md-0 " +
                        (!validationErrors.step2.proofOfIdentity ||
                          formData.step2.proofOfIdentity
                          ? ""
                          : "is-invalid")
                      }
                      type="file"
                      name="proofOfIdentity"
                      id="proofOfIdentity"
                      accept=".jpg, .jpeg, .png, .pdf, .heic"
                      onChange={(e) =>
                        handleSingleDocUpload(e, "proofOfIdentity", "PROOF_OF_ID")
                      }
                    />
                  </div>
                  <div style={{ flex: "1px" }}>
                    <Label
                      for="proofOfIdentityBack"
                      className="uploadButton fw-bold"
                      style={{
                        letterSpacing: i18n.language === "ru" ? "-1px" : "normal",
                      }}
                    >
                      {t("ProofOfIdentityBack")}
                      <div className="fileUploadButton">
                        <img src={uploadIcon} alt="uploadIcon" width={"40px"} />
                        <p className="m-0 text-center">
                          {getUploadedFileLabel(
                            formData.step2.proofOfIdentityBack
                          )}
                        </p>
                      </div>
                    </Label>
                    <Input
                      className="d-none mb-3 mb-md-0"
                      type="file"
                      name="proofOfIdentityBack"
                      id="proofOfIdentityBack"
                      accept=".jpg, .jpeg, .png, .pdf, .heic"
                      onChange={(e) =>
                        handleSingleDocUpload(
                          e,
                          "proofOfIdentityBack",
                          "PROOF_OF_ID_BACK"
                        )
                      }
                    />
                  </div>
                </>
              )}
              {isUAEResidence && (
                <>
                  <div style={{ flex: "1px" }}>
                    <Label className="uploadButton fw-bold" for="emiratesId">
                      {" "}
                      {t("EMIRATESID")}
                      <div className="fileUploadButton">
                        <img src={uploadIcon} alt="uploadIcon" width={"40px"} />
                        <p className="m-0 text-center">
                          {getUploadedFileLabel(formData.step2.emiratesId)}
                        </p>
                      </div>
                    </Label>
                    <Input
                      className={
                        "d-none mb-3 mb-md-0 form-control " +
                        (!validationErrors.step2.emiratesId ||
                          formData.step2.emiratesId
                          ? ""
                          : "is-invalid")
                      }
                      name="emiratesId"
                      id="emiratesId"
                      type="file"
                      accept=".jpg, .jpeg, .png, .pdf, .heic"
                      onChange={(e) =>
                        handleSingleDocUpload(e, "emiratesId", "EMIRATES_ID")
                      }
                    />
                  </div>
                  <div style={{ flex: "1px" }}>
                    <Label className="uploadButton fw-bold" for="emiratesIdBack">
                      {" "}
                      {t("EMIRATESIDBack")}
                      <div className="fileUploadButton">
                        <img src={uploadIcon} alt="uploadIcon" width={"40px"} />
                        <p className="m-0 text-center">
                          {getUploadedFileLabel(formData.step2.emiratesIdBack)}
                        </p>
                      </div>
                    </Label>
                    <Input
                      className={
                        "d-none mb-3 mb-md-0 form-control " +
                        (!validationErrors.step2.emiratesIdBack ||
                          formData.step2.emiratesIdBack
                          ? ""
                          : "is-invalid")
                      }
                      name="emiratesIdBack"
                      id="emiratesIdBack"
                      type="file"
                      accept=".jpg, .jpeg, .png, .pdf, .heic"
                      onChange={(e) =>
                        handleSingleDocUpload(
                          e,
                          "emiratesIdBack",
                          "EMIRATES_ID_BACK"
                        )
                      }
                    />
                  </div>
                </>
              )}
              <div style={{ flex: "1px" }}>
                <Label className="uploadButton fw-bold" for="resProofOfAddress">
                  {" "}
                  {t("ProofOfAddress")}
                  <div
                    className="tooltip"
                    style={{
                      opacity: "1",
                      zIndex: "5",
                      position: "relative",
                      top: "0px",
                      ...(i18n.language === "ar" || i18n.language === "ir"
                        ? { left: "0" }
                        : { left: "5px" }),
                    }}
                  >
                    <img
                      src={toolTipIcon}
                      alt="infoIcon"
                      className={`${(i18n.language === "ar" || i18n.language === "ir") &&
                        "ms-2"
                        }`}
                      width={"10px"}
                    />
                    <span
                      className={` ${i18n.language === "ar" || i18n.language === "ir"
                        ? "tooltiptextRightArabic"
                        : "tooltiptextRight"
                        }`}
                    >
                      {t("ToolTipHeading")} <b>{t("ToolTipBoldHeading")} </b>
                      {t("ToolTipEnding")}
                      <ul>
                        <li>{t("ToolTipList1")}</li>
                        <li>{t("ToolTipList2")}</li>
                        <li>{t("ToolTipList3")}</li>
                        <li>{t("ToolTipList4")}</li>
                        <li>{t("ToolTipList5")}</li>
                        <li>{t("ToolTipList6")}</li>
                      </ul>
                    </span>
                  </div>
                  <div
                    className={`fileUploadButton ${(i18n.language === "ar" || i18n.language === "ir") &&
                      !selectedCountryCode
                      ? "arabicTranslate"
                      : {}
                      }`}
                  >
                    <img src={uploadIcon} alt="uploadIcon" width={"40px"} />
                    <p className="m-0 text-center">
                      {getUploadedFileLabel(formData.step2.resProofOfAddress)}
                    </p>
                  </div>
                </Label>
                <div className="input-group mb-1">
                  <Input
                    className={
                      "d-none mb-3 mb-md-0 form-control " +
                      (!validationErrors.step2.resProofOfAddress ||
                        formData.step2.resProofOfAddress
                        ? ""
                        : "is-invalid")
                    }
                    name="resProofOfAddress"
                    id="resProofOfAddress"
                    type="file"
                    accept=".jpg, .jpeg, .png, .pdf, .heic"
                    onChange={(e) =>
                      handleSingleDocUpload(
                        e,
                        "resProofOfAddress",
                        "RES_PROOF_OF_ADDRESS"
                      )
                    }
                  />
                </div>
              </div>
            </div>
            <div
              className={`legal-disclosure-section ${isRtl ? "legal-disclosure-section--rtl" : ""
                }`}
            >
              {isUAEResidence && (
                <p className="legal-disclosure-panel__title">
                  {t("LegalDeclarations")}
                </p>
              )}
              <Row className="g-3 legal-disclosure-grid align-items-stretch">
                {isUAEResidence && (
                  <>
                    <Col xs={12} sm={6}>
                      <LegalYesNoField
                        label={t("PoliticallyExposedPerson")}
                        value={formData.step4.politicallyExposed}
                        onYes={() =>
                          formValueChange("step4", "politicallyExposed", true)
                        }
                        onNo={() =>
                          formValueChange("step4", "politicallyExposed", false)
                        }
                      />
                    </Col>
                    <Col xs={12} sm={6}>
                      <LegalYesNoField
                        label={t("TaxLiableOutsideUae")}
                        value={formData.step4.taxLiableOutsideUae}
                        onYes={() => setTaxLiableOutsideUae(true)}
                        onNo={() => setTaxLiableOutsideUae(false)}
                      />
                    </Col>
                    <Col xs={12} sm={6}>
                      <LegalYesNoField
                        label={t("BeneficialOwner")}
                        value={formData.step4.beneficialOwnerCode === "Y"}
                        onYes={() => setBeneficialOwner(true)}
                        onNo={() => setBeneficialOwner(false)}
                      />
                    </Col>
                  </>
                )}

                {/* <Col xs={12} sm={6}>
                  <FormGroup className="legal-field mb-0">
                    <Label className="legal-field__label">
                      {t("ModeOfPayment")}
                    </Label>
                    <Input
                      type="select"
                      name="modeOfPayment"
                      className={`legal-field__input ${validationErrors.step2.modeOfPayment &&
                        !formData.step2.modeOfPayment
                        ? "is-invalid"
                        : ""
                        }`}
                      value={
                        formData.step2.modeOfPayment ||
                        (isBankPaymentDefaultResidence
                          ? BANK_MODE_OF_PAYMENT
                          : "")
                      }
                      onChange={(e) => setModeOfPayment(e.target.value)}
                    >
                      {!isBankPaymentDefaultResidence && (
                        <option value="">{t("SelectPlaceholder")}</option>
                      )}
                      {!isBankPaymentDefaultResidence && (
                        <option value="Crypto">
                          {t("ModeOfPaymentCrypto")}
                        </option>
                      )}
                      <option value={BANK_MODE_OF_PAYMENT}>
                        {t("ModeOfPaymentNonCrypto")}
                      </option>
                    </Input>
                  </FormGroup>
                </Col> */}

                {!isUAEResidence && (
                  <Col xs={12} sm={12}>
                    <BeneficialOwnerField />
                  </Col>
                )}

                <Col xs={6} sm={6}>
                  <LegalYesNoField
                    label={t("UsCitizen")}
                    value={formData.step4.usCitizen}
                    onYes={() => formValueChange("step4", "usCitizen", true)}
                    onNo={() => {
                      formValueChange("step4", "usCitizen", false);
                      formValueChange("step2", "taxIdentificationNumber", "");
                    }}
                  />
                </Col>

                {formData.step4.usCitizen && (
                  <Col xs={12} sm={6} className="legal-field-reveal">
                    <FormGroup className="legal-field mb-0">
                      <Label className="legal-field__label">{t("SSN")}</Label>
                      <Input
                        className={`legal-field__input ${validationErrors.step2.taxIdentificationNumber &&
                          !formData.step2.taxIdentificationNumber
                          ? "is-invalid"
                          : ""
                          }`}
                        name="taxIdentificationNumber"
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        value={formData.step2.taxIdentificationNumber}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*$/.test(value)) {
                            formValueChange("step2", e.target.name, value);
                          }
                        }}
                        placeholder={t("SSNPlaceholder")}
                      />
                    </FormGroup>
                  </Col>
                )}

                {isUAEResidence && formData.step4.taxLiableOutsideUae && (
                  <Col xs={12} sm={6} className="legal-field-reveal">
                    <FormGroup className="legal-field mb-0">
                      <Label className="legal-field__label">
                        {t("TaxResidencyCountry")}
                      </Label>
                      <Select
                        className={`legal-field__select ${validationErrors.step2.taxResidency &&
                          !formData.step2.taxResidency
                          ? "is-invalid"
                          : ""
                          }`}
                        options={taxResidencyCountryOptions}
                        value={taxResidencyCountryOptions.find(
                          (option) =>
                            option.value === formData.step2.taxResidency
                        )}
                        onChange={(selected) =>
                          setTaxResidencyCountry(selected?.value || "")
                        }
                        placeholder={t("SelectPlaceholder")}
                        isClearable
                      />
                    </FormGroup>
                  </Col>
                )}
              </Row>
            </div>

            <Row
              className={`agreements-section ${isRtl ? "flex-row-reverse text-end" : ""
                } mt-3 mb-2`}
            >
              {isUAEResidence ? (
                <>
                  <AgreementCheckboxRow
                    id="infoCorrect"
                    checked={formData.step4.infoCorrect}
                    required
                    invalid={
                      validationErrors.step4.infoCorrect &&
                      !formData.step4.infoCorrect
                    }
                    onChange={(e) =>
                      formValueChange(
                        "step4",
                        "infoCorrect",
                        e.target.checked
                      )
                    }
                  >
                    {t("MoroccoInfoCorrect")}
                  </AgreementCheckboxRow>
                  <AgreementCheckboxRow
                    id="submitConsent"
                    checked={formData.step4.submitConsent}
                    required
                    invalid={
                      validationErrors.step4.submitConsent &&
                      !formData.step4.submitConsent
                    }
                    onChange={(e) =>
                      formValueChange(
                        "step4",
                        "submitConsent",
                        e.target.checked
                      )
                    }
                  >
                    <Trans
                      i18nKey="MoroccoSubmitConsent"
                      components={[
                        legalPdfLink(
                          MOROCCO_LEGAL_PDF.introductionAgreement,
                          t("IntroductionAgreement")
                        ),
                        legalPdfLink(
                          MOROCCO_LEGAL_PDF.privacyNotice,
                          t("PrivacyNoticeLink")
                        ),
                        legalPdfLink(
                          MOROCCO_LEGAL_PDF.dataProcessing,
                          t("DataProcessingAgreement")
                        ),
                      ]}
                    />
                  </AgreementCheckboxRow>
                  <Col md={12} className="marketing-consent-block">
                    <p className="marketing-consent-heading mb-2">
                      {t("MarketingConsentHeading")}
                    </p>
                    <AgreementCheckboxRow
                      id="marketingEmail"
                      checked={formData.step4.marketingEmail}
                      onChange={(e) =>
                        formValueChange(
                          "step4",
                          "marketingEmail",
                          e.target.checked
                        )
                      }
                    >
                      {t("MarketingEmail")}
                    </AgreementCheckboxRow>
                    <AgreementCheckboxRow
                      id="marketingSms"
                      checked={formData.step4.marketingSms}
                      onChange={(e) =>
                        formValueChange(
                          "step4",
                          "marketingSms",
                          e.target.checked
                        )
                      }
                    >
                      {t("MarketingSms")}
                    </AgreementCheckboxRow>
                    <AgreementCheckboxRow
                      id="marketingPhone"
                      checked={formData.step4.marketingPhone}
                      onChange={(e) =>
                        formValueChange(
                          "step4",
                          "marketingPhone",
                          e.target.checked
                        )
                      }
                    >
                      {t("MarketingPhone")}
                    </AgreementCheckboxRow>
                  </Col>
                </>
              ) : (
                <AgreementCheckboxRow
                  id="acceptTradingTerms"
                  checked={formData.step3.acceptTradingTerms}
                  invalid={
                    validationErrors.step3.acceptTradingTerms &&
                    !formData.step3.acceptTradingTerms
                  }
                  onChange={(e) =>
                    formValueChange(
                      "step3",
                      "acceptTradingTerms",
                      e.target.checked
                    )
                  }
                >
                  {t("Approve")}
                  <a
                    className="morocco-link"
                    href="https://www.godocapitalmarkets.com/wp-content/uploads/2026/09/GoDo_Terms_of_Business_v2.0.docx"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("TermsofBusiness")}
                  </a>
                  {t("Approve2")}
                </AgreementCheckboxRow>
              )}
              {
                <>
                  <Col
                    md={12}
                    className={`agreement-checkbox-row d-flex align-items-start gap-2 ${i18n.language === "ar" || i18n.language === "ir"
                      ? "flex-row-reverse text-end"
                      : ""
                      }`}
                  >
                    <Input
                      className="form-check-input agreement-checkbox-input mt-1"
                      type="checkbox"
                      name="parentId"
                      id="parentId"
                      value={formData.step1.parentId}
                      checked={couponCode}
                      onChange={() => setCopponCode(!couponCode)}
                    />
                    <Label className="agreement-checkbox-label mb-0" htmlFor="parentId">
                      {t("CouponeCode")}
                    </Label>
                  </Col>
                  {couponCode && (
                    <Col md={12}>
                      <FormGroup>
                        <Input
                          className="mb-3"
                          type="text"
                          name="couponeCode"
                          placeholder="######"
                          onChange={(e) => {
                            formValueChange(
                              "step1",
                              "couponeCode",
                              e.target.value
                            );
                          }}
                          value={formData.step1.couponeCode}
                        />
                      </FormGroup>
                    </Col>
                  )}
                </>
              }
            </Row>
          </div>
          <Col md={12}>
            <Button
              type="submit"
              onClick={(e) => submitForm(e)}
              className="submitButton"
            >
              {t("RegisterNow")}
            </Button>
          </Col>
        </CustomForm>
      </>
    </div>
  );
};
